/**
 * Content Studio admin tab + all its dependent modals/components.
 *
 * Extracted from AdminApp.jsx (Feb 2026 modularisation phase 2). Owns:
 *   - VoiceSampleButton, VoicePicker, VoiceStudio (Sarvam voice mgmt)
 *   - RolePlayPreviewModal (admin role-play dry-run)
 *   - BulkQuestionUploader (XLSX/CSV bulk ingest)
 *   - SalesPitchEditModal
 *   - ContentTab (main export — renders under /admin/content)
 *   - SalesCertAnalytics (Sales Cert cohort analytics tab body)
 *   - QuestionEditor (add/edit MCQ/SJT/open question)
 */
import { useEffect, useState, useRef } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Plus, X, Download, Upload, Wand2, Pencil, Trash2, FileText, Sparkles, BookOpen, Save, Mic, Play, Eye, Loader2, ChevronDown, ChevronUp, MessageSquare, Send, RotateCcw, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { formatApiError, API } from "../../api";
import { trackDownload, trackClick } from "../../hooks/useActivityLogger";
import { PageHeader, Loading, Err } from "./_shared";

// ---------------- Voice Sample Button (5-sec preview) ----------------
// Fetches a short Sarvam TTS clip using the auto-detected gender for a given
// scenario so trainers can lock the right persona voice before candidates
// hear it. Falls back to a role_vantage-only sample when no scenario_id is
// present (used from within the preview modal).
export function VoiceSampleButton({ scenario, label = "Play sample", compact = false }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {audio_b64, gender, speaker, provider}
  const [err, setErr] = useState("");
  const audioRef = useRef(null);

  const play = async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.post("/admin/roleplay/voice-sample", {
        scenario_id: scenario?.id,
        scenario_text: scenario?.scenario || scenario?.short_desc || null,
        role_vantage: scenario?.role_vantage || null,
      });
      setResult(r.data);
      // Autoplay on next tick so the <audio> tag mounts first
      setTimeout(() => audioRef.current?.play?.().catch(() => {}), 50);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const b64ToUrl = (b64) => {
    if (!b64) return null;
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return URL.createObjectURL(new Blob([arr], { type: "audio/wav" }));
  };

  const url = result?.audio_b64 ? b64ToUrl(result.audio_b64) : null;

  return (
    <span className="inline-flex items-center gap-1.5" data-testid={`voice-sample-${scenario?.id || "adhoc"}`}>
      <button
        onClick={play}
        disabled={loading}
        className={compact
          ? "inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#5F004B] text-white font-semibold hover:bg-[#4A003A] disabled:opacity-40"
          : "btn btn-secondary text-[11px] py-1"
        }
        title="Hear a 5-sec sample of the auto-selected Indian voice"
        data-testid={`voice-sample-btn-${scenario?.id || "adhoc"}`}
      >
        {loading ? <Loader2 size={compact ? 10 : 12} className="animate-spin" /> : <Play size={compact ? 10 : 12} />}
        {label}
      </button>
      {result && (
        <span
          className="text-[10px] uppercase tracking-wider text-[#5F004B] font-bold"
          data-testid={`voice-sample-result-${scenario?.id || "adhoc"}`}
        >
          {result.gender} · {result.speaker}
        </span>
      )}
      {url && (
        <audio ref={audioRef} controls src={url} className="h-6" data-testid={`voice-sample-audio-${scenario?.id || "adhoc"}`} />
      )}
      {err && <span className="text-[10px] text-[#EE2737]">{err}</span>}
    </span>
  );
}


// ---------------- Per-scenario Voice Picker ----------------
// Compact dropdown that lets Mercury pick a specific Sarvam Bulbul v3 voice
// for one role-play scenario. `voices` and `defaults` are cached at the
// ContentStudio level so switching between rows doesn't re-fetch. Disabled
// for hardcoded scenarios (only trainer_authored docs are editable).
export function VoicePicker({ scenario, voices, defaults, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const editable = scenario?.source === "trainer_authored";
  const current = scenario?.voice_override || "";  // "" ⇒ using default

  const save = async (vid) => {
    setSaving(true); setErr("");
    try {
      const r = await api.patch(`/admin/roleplay/scenarios/${scenario.id}/voice`, {
        voice: vid || null,
      });
      onSaved && onSaved(r.data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setSaving(false); }
  };

  const genderDefaultLabel = () => {
    // Try to reflect which global default would apply
    const inferredGender = voices?.find((v) => v.id === scenario?.voice)?.gender;
    const gid = defaults?.[inferredGender || "female"] || defaults?.female;
    const gv = voices?.find((v) => v.id === gid);
    return gv ? `Default (${gv.label})` : "Default";
  };

  if (!voices?.length) return null;

  return (
    <span className="inline-flex items-center gap-1" data-testid={`voice-picker-${scenario?.id}`}>
      <select
        value={current}
        onChange={(e) => save(e.target.value)}
        disabled={!editable || saving}
        className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#F5F5F5] text-[#525252] border border-transparent focus:border-[#EE2737] outline-none disabled:opacity-60 disabled:cursor-not-allowed"
        title={editable ? "Assign a specific Sarvam voice for this scenario" : "Hardcoded scenarios use the global default voice"}
        data-testid={`voice-picker-select-${scenario?.id}`}
      >
        <option value="">{genderDefaultLabel()}</option>
        <optgroup label="Female">
          {voices.filter((v) => v.gender === "female").map((v) => (
            <option key={v.id} value={v.id}>{v.label}</option>
          ))}
        </optgroup>
        <optgroup label="Male">
          {voices.filter((v) => v.gender === "male").map((v) => (
            <option key={v.id} value={v.id}>{v.label}</option>
          ))}
        </optgroup>
      </select>
      {saving && <Loader2 size={10} className="animate-spin text-[#5F004B]" />}
      {err && <span className="text-[10px] text-[#EE2737]" data-testid={`voice-picker-err-${scenario?.id}`}>{err}</span>}
    </span>
  );
}


// ---------------- Voice Studio (audition all Sarvam voices) ----------------
// Grid of every Sarvam Bulbul v3 voice with a play-sample button and a "Set
// as default" action. The active global default per gender is highlighted so
// trainers can see at a glance which voice candidates will hear by default.
export function VoiceStudio({ voices, defaults, onDefaultsChanged }) {
  const [playingId, setPlayingId] = useState(null);
  const [audioMap, setAudioMap] = useState({}); // { voice_id: audio_b64 }
  const [err, setErr] = useState("");
  const [savingGender, setSavingGender] = useState(null);
  const [genderFilter, setGenderFilter] = useState("all"); // all | female | male
  const audioRefs = useRef({});

  const b64ToUrl = (b64) => {
    if (!b64) return null;
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return URL.createObjectURL(new Blob([arr], { type: "audio/wav" }));
  };

  const play = async (voice) => {
    if (audioMap[voice.id]) {
      audioRefs.current[voice.id]?.play?.().catch(() => {});
      return;
    }
    setPlayingId(voice.id); setErr("");
    try {
      const r = await api.post("/admin/roleplay/voice-sample", { speaker: voice.id });
      setAudioMap((prev) => ({ ...prev, [voice.id]: r.data.audio_b64 }));
      setTimeout(() => audioRefs.current[voice.id]?.play?.().catch(() => {}), 60);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setPlayingId(null);
    }
  };

  const makeDefault = async (voice) => {
    setSavingGender(voice.gender); setErr("");
    try {
      const r = await api.patch("/admin/settings/roleplay-voices", { [voice.gender]: voice.id });
      onDefaultsChanged && onDefaultsChanged(r.data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setSavingGender(null); }
  };

  const list = (voices || []).filter((v) => genderFilter === "all" || v.gender === genderFilter);
  const femaleDefault = voices?.find((v) => v.id === defaults?.female);
  const maleDefault = voices?.find((v) => v.id === defaults?.male);

  return (
    <div className="space-y-4" data-testid="voice-studio">
      <div className="card p-5 bg-[#F5F3EE]">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#5F004B]">Global default voices</div>
            <div className="text-sm text-[#0A0A0A] mt-1">
              Used for every scenario that doesn&apos;t have its own voice override. Switch defaults here to change the voice candidates hear across the board.
            </div>
            <div className="mt-3 flex gap-4 flex-wrap text-xs text-[#525252]">
              <span data-testid="voice-studio-default-female">
                <strong className="uppercase tracking-wider text-[10px] mr-1">Female:</strong>
                {femaleDefault ? <span className="font-bold text-[#0A0A0A]">{femaleDefault.label}</span> : "—"}
              </span>
              <span data-testid="voice-studio-default-male">
                <strong className="uppercase tracking-wider text-[10px] mr-1">Male:</strong>
                {maleDefault ? <span className="font-bold text-[#0A0A0A]">{maleDefault.label}</span> : "—"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2" data-testid="voice-studio-filter">
            {["all", "female", "male"].map((g) => (
              <button
                key={g}
                onClick={() => setGenderFilter(g)}
                className={`text-[10px] uppercase tracking-wider px-3 py-1 border ${
                  genderFilter === g
                    ? "bg-[#EE2737] text-white border-[#EE2737]"
                    : "bg-white text-[#525252] border-[#E5E7EB] hover:border-[#EE2737]"
                }`}
                data-testid={`voice-studio-filter-${g}`}
              >
                {g === "all" ? "All" : g === "female" ? "Female" : "Male"}
              </button>
            ))}
          </div>
        </div>
        {err && <div className="text-xs text-[#EE2737] mt-3" data-testid="voice-studio-err">{err}</div>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map((v) => {
          const isDefault = defaults?.[v.gender] === v.id;
          const url = audioMap[v.id] ? b64ToUrl(audioMap[v.id]) : null;
          return (
            <div
              key={v.id}
              className={`p-4 border ${isDefault ? "border-[#EE2737] bg-[#FEF2F2]" : "border-[#E5E7EB] bg-white"} flex flex-col gap-2`}
              data-testid={`voice-card-${v.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-heading text-base font-bold text-[#0A0A0A]">{v.label}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#878787]">{v.gender} · {v.id}</div>
                </div>
                {isDefault && (
                  <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 bg-[#EE2737] text-white font-bold" data-testid={`voice-card-badge-${v.id}`}>
                    Default
                  </span>
                )}
              </div>
              <div className="text-xs text-[#525252] flex-1">{v.description}</div>
              <div className="flex gap-2 items-center pt-1">
                <button
                  onClick={() => play(v)}
                  disabled={playingId === v.id}
                  className="btn btn-secondary text-[11px] py-1"
                  data-testid={`voice-card-play-${v.id}`}
                >
                  {playingId === v.id ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                  {audioMap[v.id] ? "Replay" : "Sample"}
                </button>
                <button
                  onClick={() => makeDefault(v)}
                  disabled={isDefault || savingGender === v.gender}
                  className="text-[11px] px-3 py-1 border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid={`voice-card-default-${v.id}`}
                >
                  {savingGender === v.gender ? "Saving…" : isDefault ? "Current default" : `Set as ${v.gender} default`}
                </button>
              </div>
              {url && (
                <audio
                  ref={(el) => { if (el) audioRefs.current[v.id] = el; }}
                  controls
                  src={url}
                  className="h-6 w-full mt-1"
                  data-testid={`voice-card-audio-${v.id}`}
                />
              )}
            </div>
          );
        })}
      </div>

      {(!voices || voices.length === 0) && (
        <div className="card p-10 text-center text-[#878787] text-sm">
          Loading Sarvam voice catalogue…
        </div>
      )}
    </div>
  );
}



// ---------------- Role-Play Preview Modal ----------------
export function RolePlayPreviewModal({ scenario, onClose }) {
  const [sample, setSample] = useState("");
  const [withAudio, setWithAudio] = useState(false);
  const [preview, setPreview] = useState(null); // {opening_message, ai_reply, opening_audio_b64, ai_reply_audio_b64}
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const openingAudioRef = useRef(null);
  const replyAudioRef = useRef(null);

  const runPreview = async (includeSample) => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/admin/roleplay/preview", {
        exercise_id: scenario.id,
        sample_message: includeSample ? sample.trim() : null,
        with_audio: withAudio,
      });
      setPreview(r.data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Auto-load opening on mount
  useEffect(() => { runPreview(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const b64ToUrl = (b64) => {
    if (!b64) return null;
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return URL.createObjectURL(new Blob([arr], { type: "audio/wav" }));
  };

  const openingUrl = preview?.opening_audio_b64 ? b64ToUrl(preview.opening_audio_b64) : null;
  const replyUrl = preview?.ai_reply_audio_b64 ? b64ToUrl(preview.ai_reply_audio_b64) : null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 fade-in" data-testid="roleplay-preview-modal">
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-[#E5E7EB] px-6 py-4 flex items-start justify-between gap-4 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#EE2737] font-bold">ROLE-PLAY PREVIEW · {scenario.source === "trainer_authored" ? "TRAINER-AUTHORED" : "HARDCODED"}</div>
            <h3 className="font-heading text-lg font-bold text-[#0A0A0A] mt-0.5">{scenario.name}</h3>
            <div className="text-[11px] text-[#525252] mt-1">
              voice: <span className="font-semibold">{scenario.voice}</span> · max turns: {scenario.max_turns} · duration: {scenario.duration_min} min
            </div>
            <div className="mt-2">
              <VoiceSampleButton scenario={scenario} label="Play 5-sec voice sample" />
            </div>
          </div>
          <button onClick={onClose} className="text-[#878787] hover:text-[#0A0A0A]" data-testid="preview-close"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-4">
          {err && <div className="text-xs text-[#EE2737] bg-[#FEE2E2] px-3 py-2 border border-[#FCA5A5]" data-testid="preview-error">{err}</div>}

          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs text-[#525252] cursor-pointer" data-testid="preview-with-audio-label">
              <input
                type="checkbox"
                checked={withAudio}
                onChange={(e) => { setWithAudio(e.target.checked); setPreview(null); }}
                data-testid="preview-with-audio"
              />
              Fetch AI voice audio (adds ~5-10s)
            </label>
          </div>

          <div className="border border-[#E5E7EB] bg-[#F9FAFB] p-4 space-y-2" data-testid="preview-opening-block">
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#0A0A0A]">Opening (AI persona)</div>
            {!preview && !busy && <div className="text-xs text-[#878787]">Loading opening…</div>}
            {busy && !preview && <div className="text-xs text-[#525252] flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Fetching opening…</div>}
            {preview?.opening_message && (
              <>
                <div className="text-sm text-[#0A0A0A] whitespace-pre-wrap" data-testid="preview-opening-text">{preview.opening_message}</div>
                {openingUrl && (
                  <audio ref={openingAudioRef} controls src={openingUrl} className="w-full mt-2" data-testid="preview-opening-audio" />
                )}
              </>
            )}
          </div>

          <div className="border border-[#E5E7EB] p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#0A0A0A]">Simulate a candidate turn</div>
            <textarea
              value={sample}
              onChange={(e) => setSample(e.target.value)}
              placeholder="Type a sample candidate response (e.g., 'Lock the renewal on Monday first — biggest booking, highest probability.')"
              rows={3}
              className="w-full text-sm border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:border-[#EE2737]"
              data-testid="preview-sample-input"
            />
            <button
              onClick={() => runPreview(true)}
              disabled={busy || !sample.trim()}
              className="btn btn-primary text-xs disabled:opacity-40"
              data-testid="preview-run-turn"
            >
              {busy ? (<><Loader2 size={12} className="animate-spin" /> Generating…</>) : (<><Play size={12} /> Simulate Turn</>)}
            </button>
            {preview?.ai_reply && (
              <div className="border-l-2 border-[#EE2737] pl-3 pt-1 mt-2" data-testid="preview-ai-reply">
                <div className="text-[10px] uppercase tracking-wider text-[#EE2737] font-bold mb-1">AI Reply</div>
                <div className="text-sm text-[#0A0A0A] whitespace-pre-wrap">{preview.ai_reply}</div>
                {replyUrl && (
                  <audio ref={replyAudioRef} controls src={replyUrl} className="w-full mt-2" data-testid="preview-reply-audio" />
                )}
              </div>
            )}
          </div>

          <div className="text-[10px] text-[#878787] italic">
            Preview turns are ephemeral — nothing is saved to the candidate's transcript.
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------------- Bulk Question Uploader (XLSX) ----------------
export function BulkQuestionUploader({ onDone, onClose }) {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [committed, setCommitted] = useState(false);
  const inputRef = useRef(null);

  const runUpload = async (commit) => {
    if (!file) { setErr("Please choose an .xlsx file first."); return; }
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("commit", commit ? "true" : "false");
      const r = await api.post("/admin/questions/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReport(r.data);
      if (commit) {
        setCommitted(true);
        setTimeout(() => onDone && onDone(), 800);
      }
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    api.get("/admin/questions/template", { responseType: "blob" }).then((r) => {
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "COMPASS_Question_Authoring_Template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }).catch((e) => setErr(formatApiError(e)));
    trackDownload && trackDownload("question_template.xlsx");
  };

  const totalOk = report?.totals?.ok || 0;
  const totalFail = report?.totals?.failed || 0;

  return (
    <div className="card p-6 space-y-4 fade-in" data-testid="bulk-question-uploader">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-heading text-lg font-bold text-[#0A0A0A]">Bulk Upload Question Bank</h3>
          <p className="text-xs text-[#525252] mt-1 max-w-2xl">
            Upload the COMPASS Question Authoring XLSX with five tabs (MCQ / Scenario /
            True-False, Situational SJT, Role-Based Decision Scenarios, Short & Case
            Answer, plus a Metadata_Blueprint tab). The uploader will dry-run first —
            no data is written until you click Commit.
          </p>
        </div>
        <button onClick={onClose} className="text-[#878787] hover:text-[#0A0A0A]" data-testid="bulk-upload-close">
          <X size={18} />
        </button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={downloadTemplate} className="btn btn-secondary text-xs" data-testid="download-question-template">
          <Download size={14} /> Download Template
        </button>
        <label className="btn btn-secondary text-xs cursor-pointer" data-testid="bulk-upload-file-pick">
          <Upload size={14} /> {file ? file.name : "Choose .xlsx file"}
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setReport(null); setCommitted(false); }}
            data-testid="bulk-upload-file-input"
          />
        </label>
        <button
          onClick={() => runUpload(false)}
          disabled={busy || !file}
          className="btn btn-secondary text-xs disabled:opacity-40"
          data-testid="bulk-upload-dryrun"
        >
          {busy && !committed ? "Validating…" : "Dry-Run (Validate)"}
        </button>
        <button
          onClick={() => runUpload(true)}
          disabled={busy || !file || !report || totalOk === 0}
          className="btn btn-primary text-xs disabled:opacity-40"
          data-testid="bulk-upload-commit"
        >
          {committed ? "Committed ✓" : busy ? "Committing…" : `Commit ${totalOk} rows`}
        </button>
      </div>

      {err && <div className="text-xs text-[#EE2737] bg-[#FEE2E2] px-3 py-2 border border-[#FCA5A5]" data-testid="bulk-upload-error">{err}</div>}

      {report && (
        <div className="border border-[#E5E7EB] bg-[#F9FAFB] p-4 space-y-3" data-testid="bulk-upload-report">
          <div className="flex items-center gap-4 text-xs">
            <span className="font-bold text-[#0A0A0A]">Summary:</span>
            <span className="px-2 py-0.5 bg-[#D1FAE5] text-[#065F46] font-semibold" data-testid="bulk-total-ok">
              {totalOk} valid rows
            </span>
            <span className={`px-2 py-0.5 font-semibold ${totalFail > 0 ? "bg-[#FEE2E2] text-[#991B1B]" : "bg-[#F3F4F6] text-[#525252]"}`} data-testid="bulk-total-failed">
              {totalFail} failed
            </span>
            {report.commit && (
              <span className="px-2 py-0.5 bg-[#DBEAFE] text-[#1E40AF] font-semibold" data-testid="bulk-total-committed">
                {report.totals.committed} committed
              </span>
            )}
          </div>
          {Object.entries(report.tabs).map(([tab, v]) => {
            const okCount = (v.ok || []).length;
            const errCount = (v.errors || []).length;
            return (
              <div key={tab} className="border border-[#E5E7EB] bg-white p-3" data-testid={`bulk-tab-${tab}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] uppercase tracking-wider font-bold text-[#0A0A0A]">{tab}</span>
                  <span className="text-[11px] text-[#525252]">
                    {v.skipped ? v.skipped : `${okCount} ok · ${errCount} errors`}
                  </span>
                </div>
                {errCount > 0 && (
                  <div className="space-y-1 max-h-40 overflow-auto">
                    {v.errors.map((e, i) => (
                      <div key={i} className="text-[11px] text-[#991B1B] bg-[#FEF2F2] px-2 py-1 border-l-2 border-[#EE2737]" data-testid={`bulk-err-${tab}-${e.row}`}>
                        <span className="font-mono font-bold">Row {e.row}:</span> {e.error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ---------------- Sales Pitch Edit Modal ----------------
// Compact editor for a single trainer-authored pitch prompt. Fields are
// grouped: metadata (cohort / competency / difficulty), timing, prompt text,
// and the 3-anchor rubric (Target · Proficient · Anti-pattern). Save fires
// a PATCH; only changed fields are sent so trainer-authored notes are not
// clobbered by empty strings.
export function SalesPitchEditModal({ pitch, onClose, onSaved }) {
  const rubric = pitch.rubric || {};
  const [form, setForm] = useState({
    cohort: pitch.cohort || "",
    competency: pitch.competency || "",
    difficulty: pitch.difficulty || "",
    tags: (pitch.tags || []).join(", "),
    prompt: pitch.prompt || "",
    prep_instructions: pitch.prep_instructions || "",
    prep_seconds: pitch.prep_seconds || 120,
    record_seconds: pitch.record_seconds || 300,
    marks: pitch.marks || 25,
    target: rubric.target || "",
    proficient: rubric.proficient || "",
    anti_pattern: rubric.anti_pattern || "",
    sample_strong_response: pitch.sample_strong_response || "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true); setErr("");
    try {
      const payload = { ...form };
      payload.tags = form.tags.split(",").map((s) => s.trim()).filter(Boolean);
      payload.prep_seconds = Number(form.prep_seconds) || 0;
      payload.record_seconds = Number(form.record_seconds) || 0;
      payload.marks = Number(form.marks) || 0;
      await api.patch(`/admin/salespitch/prompts/${pitch.id}`, payload);
      // Reflect the update locally without a round-trip refetch. Rubric
      // fields need to be nested back under `rubric` so the list-render
      // shape stays consistent with GET /admin/salespitch/prompts.
      onSaved({
        id: pitch.id,
        ...payload,
        rubric: {
          ...(pitch.rubric || {}),
          target: payload.target,
          proficient: payload.proficient,
          anti_pattern: payload.anti_pattern,
        },
      });
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-[#0A0A0A]/80 backdrop-blur-sm p-4 sm:p-8 overflow-y-auto"
      data-testid="salespitch-edit-modal"
    >
      <div className="max-w-3xl mx-auto bg-white border-2 border-[#0A0A0A] shadow-2xl">
        <div className="sticky top-0 bg-[#5F004B] text-white px-5 py-4 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FFC600] font-bold">Sales Pitch · Edit prompt</div>
            <h2 className="font-heading font-black text-xl mt-1">{pitch.id}</h2>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white p-1" data-testid="salespitch-edit-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 sm:p-7 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="overline block mb-1">Cohort</span>
              <input value={form.cohort} onChange={(e) => patch("cohort", e.target.value)} className="input" data-testid="pitch-edit-cohort" />
            </label>
            <label className="block">
              <span className="overline block mb-1">Competency</span>
              <input value={form.competency} onChange={(e) => patch("competency", e.target.value)} className="input" data-testid="pitch-edit-competency" />
            </label>
            <label className="block">
              <span className="overline block mb-1">Difficulty</span>
              <input value={form.difficulty} onChange={(e) => patch("difficulty", e.target.value)} className="input" data-testid="pitch-edit-difficulty" placeholder="easy · medium · hard" />
            </label>
          </div>

          <label className="block">
            <span className="overline block mb-1">Tags (comma separated)</span>
            <input value={form.tags} onChange={(e) => patch("tags", e.target.value)} className="input" data-testid="pitch-edit-tags" />
          </label>

          <label className="block">
            <span className="overline block mb-1">Prompt · what the candidate is asked to pitch</span>
            <textarea value={form.prompt} onChange={(e) => patch("prompt", e.target.value)} rows={4} className="input font-body" data-testid="pitch-edit-prompt" />
          </label>

          <label className="block">
            <span className="overline block mb-1">Prep instructions (shown during prep timer)</span>
            <textarea value={form.prep_instructions} onChange={(e) => patch("prep_instructions", e.target.value)} rows={2} className="input font-body" data-testid="pitch-edit-prep-instructions" />
          </label>

          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="overline block mb-1">Prep seconds</span>
              <input type="number" value={form.prep_seconds} onChange={(e) => patch("prep_seconds", e.target.value)} className="input" data-testid="pitch-edit-prep-seconds" />
            </label>
            <label className="block">
              <span className="overline block mb-1">Record seconds</span>
              <input type="number" value={form.record_seconds} onChange={(e) => patch("record_seconds", e.target.value)} className="input" data-testid="pitch-edit-record-seconds" />
            </label>
            <label className="block">
              <span className="overline block mb-1">Marks (max)</span>
              <input type="number" value={form.marks} onChange={(e) => patch("marks", e.target.value)} className="input" data-testid="pitch-edit-marks" />
            </label>
          </div>

          <div className="border-t border-[#E5E7EB] pt-5 space-y-3">
            <div className="overline text-[#5F004B]">Rubric anchors · scored by Claude</div>
            <label className="block">
              <span className="text-xs font-bold text-[#059669] uppercase tracking-wider">Score 5 · Target</span>
              <textarea value={form.target} onChange={(e) => patch("target", e.target.value)} rows={3} className="input font-body mt-1" data-testid="pitch-edit-target" />
            </label>
            <label className="block">
              <span className="text-xs font-bold text-[#F59E0B] uppercase tracking-wider">Score 3 · Proficient</span>
              <textarea value={form.proficient} onChange={(e) => patch("proficient", e.target.value)} rows={3} className="input font-body mt-1" data-testid="pitch-edit-proficient" />
            </label>
            <label className="block">
              <span className="text-xs font-bold text-[#EE2737] uppercase tracking-wider">Score 1 · Anti-pattern</span>
              <textarea value={form.anti_pattern} onChange={(e) => patch("anti_pattern", e.target.value)} rows={3} className="input font-body mt-1" data-testid="pitch-edit-anti-pattern" />
            </label>
          </div>

          <label className="block">
            <span className="overline block mb-1">Sample strong response (internal · not shown to candidate)</span>
            <textarea value={form.sample_strong_response} onChange={(e) => patch("sample_strong_response", e.target.value)} rows={3} className="input font-body" data-testid="pitch-edit-sample" />
          </label>

          {err && (
            <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-xs px-3 py-2" data-testid="pitch-edit-err">{err}</div>
          )}

          <div className="flex gap-3 border-t border-[#E5E7EB] pt-5">
            <button onClick={onClose} className="btn btn-ghost" data-testid="pitch-edit-cancel">Cancel</button>
            <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="pitch-edit-save">
              <Save size={14} /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------------- Content Studio (Question CRUD) ----------------
export default function ContentTab() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("sjt");
  const [editing, setEditing] = useState(null); // {type, question?}
  const [err, setErr] = useState("");
  const [showBulk, setShowBulk] = useState(false);
  const [rolePlays, setRolePlays] = useState(null);
  const [salesPitches, setSalesPitches] = useState(null);
  const [previewing, setPreviewing] = useState(null); // scenario object
  const [editingPitch, setEditingPitch] = useState(null); // sales-pitch prompt object being edited
  const [analytics, setAnalytics] = useState(null);
  const [voices, setVoices] = useState(null);
  const [voiceDefaults, setVoiceDefaults] = useState(null);

  const load = () => api.get("/admin/questions").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e)));
  const loadRolePlays = () => api.get("/admin/roleplay/scenarios").then((r) => setRolePlays(r.data)).catch((e) => setErr(formatApiError(e)));
  const loadSalesPitches = () => api.get("/admin/salespitch/prompts").then((r) => setSalesPitches(r.data)).catch((e) => setErr(formatApiError(e)));
  const loadVoices = () => api.get("/admin/roleplay/voices").then((r) => {
    setVoices(r.data.voices || []);
    setVoiceDefaults(r.data.defaults || {});
  }).catch((e) => setErr(formatApiError(e)));
  const loadAnalytics = (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.region) params.set("region", filters.region);
    if (filters.competency) params.set("competency", filters.competency);
    if (filters.bu) params.set("bu", filters.bu);
    if (filters.manager) params.set("manager", filters.manager);
    const qs = params.toString() ? `?${params.toString()}` : "";
    return api.get(`/admin/salescert/analytics${qs}`).then((r) => setAnalytics(r.data)).catch((e) => setErr(formatApiError(e)));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === "roleplay" && rolePlays === null) loadRolePlays(); }, [tab, rolePlays]);
  useEffect(() => { if ((tab === "roleplay" || tab === "voices") && voices === null) loadVoices(); }, [tab, voices]);
  useEffect(() => { if (tab === "salespitch" && salesPitches === null) loadSalesPitches(); }, [tab, salesPitches]);
  useEffect(() => { if (tab === "salescert" && analytics === null) loadAnalytics(); }, [tab, analytics]);

  const removeRolePlay = async (id, source) => {
    if (source !== "trainer_authored") return; // guard on FE; BE also enforces
    if (!window.confirm("Delete this trainer-authored role-play scenario? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/roleplay/scenarios/${id}`);
      await loadRolePlays();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const downloadBatchReview = () => {
    api.get("/admin/roleplay/batch-review.pdf", { responseType: "blob" }).then((r) => {
      // Prefer the server's Content-Disposition filename so client/server stay in sync
      let filename = "COMPASS_RolePlay_BatchReview.pdf";
      const cd = r.headers?.["content-disposition"] || r.headers?.["Content-Disposition"];
      const match = cd && /filename="?([^"]+)"?/.exec(cd);
      if (match) filename = match[1];
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }).catch((e) => setErr(formatApiError(e)));
  };

  const remove = async (type, id) => {
    if (!window.confirm("Delete this question? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/questions/${type}/${id}`);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  if (err) return <Err msg={err} />;
  if (!data) return <Loading />;

  const compLabel = (k) => data.competencies.find((c) => c.key === k)?.name || k;
  const items = data[tab] || [];

  return (
    <div className="space-y-8 fade-in">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <PageHeader
          eyebrow="CONTENT STUDIO · ITEM BANK"
          title="Manage SJT, MCQ, Open & Role-Plays"
          subtitle="Author and curate the items VI participants see. Changes apply immediately to all new submissions. Existing scored results are not recomputed."
        />
        <div className="flex gap-2">
          {tab === "roleplay" && rolePlays && rolePlays.length > 0 && (
            <button onClick={downloadBatchReview} className="btn btn-secondary" data-testid="roleplay-batch-review-btn">
              <FileText size={14} /> Batch Review PDF
            </button>
          )}
          <button onClick={() => setShowBulk(!showBulk)} className="btn btn-secondary" data-testid="bulk-upload-questions-toggle">
            <Upload size={14} /> Bulk Upload
          </button>
          {tab !== "roleplay" && tab !== "salescert" && tab !== "salespitch" && tab !== "voices" && (
            <button onClick={() => setEditing({ type: tab })} className="btn btn-primary" data-testid="add-question">
              <Plus size={14} /> Add {tab.toUpperCase()}
            </button>
          )}
        </div>
      </div>

      {showBulk && (
        <BulkQuestionUploader onDone={() => { setShowBulk(false); load(); if (tab === "roleplay") loadRolePlays(); if (tab === "salespitch") loadSalesPitches(); }} onClose={() => setShowBulk(false)} />
      )}

      {editingPitch && (
        <SalesPitchEditModal
          pitch={editingPitch}
          onClose={() => setEditingPitch(null)}
          onSaved={(updated) => {
            setSalesPitches((prev) => (prev || []).map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));
            setEditingPitch(null);
          }}
        />
      )}

      <div className="flex border-b border-[#E5E7EB] overflow-x-auto" data-testid="content-tabs">
        {[
          { k: "sjt", n: "Situational Judgment", c: data.sjt.length },
          { k: "mcq", n: "Knowledge MCQ", c: data.mcq.length },
          { k: "open", n: "Open Response", c: data.open.length },
          { k: "salespitch", n: "Sales Pitch", c: salesPitches ? salesPitches.length : "—" },
          { k: "roleplay", n: "Role-Plays", c: rolePlays ? rolePlays.length : "—" },
          { k: "voices", n: "Voice Studio", c: voices ? voices.length : "—" },
          { k: "salescert", n: "Sales Cert Analytics", c: analytics ? analytics.attempts.fully_completed : "—" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-5 py-3 text-sm font-heading font-bold border-b-2 -mb-[1px] flex items-center gap-2 whitespace-nowrap ${
              tab === t.k ? "border-[#EE2737] text-[#0A0A0A]" : "border-transparent text-[#878787] hover:text-[#0A0A0A]"
            }`}
            data-testid={`content-tab-${t.k}`}
          >
            {t.k === "roleplay" ? <Mic size={14} /> : t.k === "voices" ? <Play size={14} /> : <BookOpen size={14} />} {t.n} <span className="text-[11px] text-[#878787]">({t.c})</span>
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {tab === "voices" ? (
          <VoiceStudio
            voices={voices}
            defaults={voiceDefaults}
            onDefaultsChanged={(next) => setVoiceDefaults(next)}
          />
        ) : tab === "salescert" ? (
          <SalesCertAnalytics analytics={analytics} compLabel={compLabel} onFilterChange={loadAnalytics} />
        ) : tab === "salespitch" ? (
          salesPitches === null ? (
            <div className="card p-10 text-center text-[#878787] text-sm">Loading sales-pitch prompts…</div>
          ) : salesPitches.length === 0 ? (
            <div className="card p-10 text-center text-[#878787] text-sm">
              No sales-pitch prompts yet. Use &quot;Bulk Upload&quot; to import the SalesPitch_Prompts tab of the trainer XLSX.
            </div>
          ) : salesPitches.map((p) => (
            <div key={p.id} className="card p-5" data-testid={`salespitch-row-${p.id}`}>
              <div className="flex justify-between gap-4 mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#EE2737]">{p.id}</span>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#F3F4F6] text-[#525252] font-semibold">{compLabel(p.competency)}</span>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#F5F5F5] text-[#525252]">{p.cohort}</span>
                  {p.difficulty && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#F5F5F5] text-[#525252]">{p.difficulty}</span>}
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#DBEAFE] text-[#1E40AF] font-semibold">Trainer</span>
                </div>
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <span className="text-[10px] text-[#878787]">{p.prep_seconds || 120}s prep · {p.record_seconds || 120}s record · {p.marks || 25} marks</span>
                  <button
                    onClick={() => setEditingPitch(p)}
                    className="p-1.5 hover:bg-[#F5F5F5] border border-transparent hover:border-[#E5E7EB]"
                    title="Edit prompt / rubric"
                    data-testid={`salespitch-edit-${p.id}`}
                  >
                    <Pencil size={14} className="text-[#525252]" />
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete pitch prompt "${p.id}"? This can't be undone.`)) return;
                      try {
                        await api.delete(`/admin/salespitch/prompts/${p.id}`);
                        setSalesPitches((prev) => (prev || []).filter((row) => row.id !== p.id));
                      } catch (e) { setErr(formatApiError(e)); }
                    }}
                    className="p-1.5 hover:bg-[#FEE2E2] border border-transparent hover:border-[#FCA5A5]"
                    title="Delete this pitch prompt"
                    data-testid={`salespitch-delete-${p.id}`}
                  >
                    <Trash2 size={14} className="text-[#DC2626]" />
                  </button>
                </div>
              </div>
              <div className="text-sm text-[#0A0A0A] leading-relaxed mb-3">{p.prompt || p.prompt_preview}</div>
              {p.prep_instructions && (
                <div className="text-xs text-[#525252] mb-3 italic border-l-2 border-[#E5E7EB] pl-3">
                  <strong>Prep guidance:</strong> {p.prep_instructions}
                </div>
              )}
              <details className="text-xs">
                <summary className="cursor-pointer font-bold uppercase tracking-widest text-[#525252] py-1">Scoring rubric (BARS anchors)</summary>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="p-3 bg-[#E6F4EC] border-l-2 border-[#008A3D]">
                    <div className="text-[10px] uppercase font-bold text-[#008A3D] mb-1">Score 5 · Target</div>
                    <div className="text-[11px] text-[#0A0A0A]">{p.target || p.rubric?.target || "—"}</div>
                  </div>
                  <div className="p-3 bg-[#FEF3C7] border-l-2 border-[#D97706]">
                    <div className="text-[10px] uppercase font-bold text-[#D97706] mb-1">Score 3 · Proficient</div>
                    <div className="text-[11px] text-[#0A0A0A]">{p.proficient || p.rubric?.proficient || "—"}</div>
                  </div>
                  <div className="p-3 bg-[#FEE2E2] border-l-2 border-[#DC2626]">
                    <div className="text-[10px] uppercase font-bold text-[#DC2626] mb-1">Score 1 · Anti-pattern</div>
                    <div className="text-[11px] text-[#0A0A0A]">{p.anti_pattern || p.rubric?.anti_pattern || "—"}</div>
                  </div>
                </div>
                {(p.sample_strong_response || p.has_sample) && p.sample_strong_response && (
                  <div className="mt-3 p-3 bg-[#F5F3EE] border-l-2 border-[#EE2737]">
                    <div className="text-[10px] uppercase font-bold text-[#EE2737] mb-1">Sample strong response</div>
                    <div className="text-[11px] text-[#0A0A0A] whitespace-pre-wrap">{p.sample_strong_response}</div>
                  </div>
                )}
              </details>
            </div>
          ))
        ) : tab === "roleplay" ? (
          rolePlays === null ? (
            <div className="card p-10 text-center text-[#878787] text-sm">Loading role-play scenarios…</div>
          ) : rolePlays.length === 0 ? (
            <div className="card p-10 text-center text-[#878787] text-sm">
              No role-play scenarios yet. Use "Bulk Upload" to import the RoleDecision_Scenarios tab of the trainer XLSX.
            </div>
          ) : rolePlays.map((rp) => (
            <div key={rp.id} className="card p-5" data-testid={`roleplay-row-${rp.id}`}>
              <div className="flex justify-between gap-4 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#EE2737]">{rp.id}</span>
                  {(rp.competencies || []).map((c) => (
                    <span key={c} className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#F3F4F6] text-[#525252] font-semibold">{compLabel(c)}</span>
                  ))}
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 font-semibold ${
                    rp.source === "trainer_authored"
                      ? "bg-[#DBEAFE] text-[#1E40AF]"
                      : "bg-[#F5F5F5] text-[#525252]"
                  }`} data-testid={`roleplay-source-${rp.id}`}>
                    {rp.source === "trainer_authored" ? "Trainer" : "Hardcoded"}
                  </span>
                  <VoicePicker
                    scenario={rp}
                    voices={voices}
                    defaults={voiceDefaults}
                    onSaved={(updated) => {
                      setRolePlays((prev) => (prev || []).map((row) =>
                        row.id === updated.id
                          ? { ...row, voice_override: updated.voice || null, voice: updated.resolved_speaker || row.voice }
                          : row
                      ));
                    }}
                  />
                  {rp.role_vantage && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#F5F5F5] text-[#525252]">as: {rp.role_vantage}</span>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  <VoiceSampleButton scenario={rp} compact label="Voice sample" />
                  <button onClick={() => setPreviewing(rp)} className="btn btn-secondary text-[11px] py-1" data-testid={`preview-${rp.id}`}>
                    <Play size={12} /> Preview
                  </button>
                  <button
                    onClick={() => removeRolePlay(rp.id, rp.source)}
                    disabled={rp.source !== "trainer_authored"}
                    className={`text-[#525252] hover:text-[#EE2737] disabled:opacity-30 disabled:cursor-not-allowed`}
                    title={rp.source === "trainer_authored" ? "Delete this trainer-authored scenario" : "Hardcoded scenarios cannot be deleted"}
                    data-testid={`delete-roleplay-${rp.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="text-sm text-[#0A0A0A] font-medium mb-1">{rp.name}</div>
              <div className="text-xs text-[#525252]">{rp.short_desc}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#878787] mt-2">
                max turns: {rp.max_turns} · duration: {rp.duration_min} min
              </div>
            </div>
          ))
        ) : items.length === 0 ? (
          <div className="card p-10 text-center text-[#878787] text-sm">
            No questions in this bank yet. Click "Add {tab.toUpperCase()}" to author the first one.
          </div>
        ) : items.map((q) => (
          <div key={q.id} className="card p-5" data-testid={`question-row-${q.id}`}>
            <div className="flex justify-between gap-4 mb-2">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider font-bold text-[#EE2737]">{q.id}</span>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#F3F4F6] text-[#525252] font-semibold">{compLabel(q.competency)}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing({ type: tab, question: q })} className="text-[#525252] hover:text-[#EE2737]" data-testid={`edit-${q.id}`}>
                  <Pencil size={14} />
                </button>
                <button onClick={() => remove(tab, q.id)} className="text-[#525252] hover:text-[#EE2737]" data-testid={`delete-${q.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <div className="text-sm text-[#0A0A0A] font-medium mb-2">
              {tab === "sjt" ? q.scenario : tab === "mcq" ? q.question : q.prompt}
            </div>
            {tab !== "open" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {q.options.map((o) => {
                  const isCorrect = tab === "mcq" && q.correct_option === o.key;
                  const sjtScore = tab === "sjt" ? q.scoring_key?.[o.key] : null;
                  return (
                    <div key={o.key} className={`text-[12px] px-3 py-2 border ${
                      isCorrect ? "border-[#10B981] bg-[#D1FAE5]" :
                      sjtScore === 5 ? "border-[#10B981] bg-[#D1FAE5]" :
                      sjtScore === 1 ? "border-[#E5E7EB] bg-[#FEE2E2]" :
                      "border-[#E5E7EB] bg-white"
                    }`}>
                      <span className="font-bold mr-2">{o.key}.</span>
                      <span className="text-[#0A0A0A]">{o.text}</span>
                      {sjtScore !== null && sjtScore !== undefined && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-[#878787] font-semibold">score {sjtScore}</span>
                      )}
                      {isCorrect && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#065F46] font-bold">correct</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <QuestionEditor
          type={editing.type}
          question={editing.question}
          competencies={data.competencies}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}

      {previewing && (
        <RolePlayPreviewModal
          scenario={previewing}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  );
}

// ---------------- Sales Cert Analytics (Admin) ----------------
const SalesCertStat = ({ label, value, sub }) => (
  <div className="card p-5">
    <div className="overline mb-1">{label}</div>
    <div className="font-heading font-black text-3xl">{value ?? "—"}</div>
    {sub && <div className="text-[11px] text-[#878787] mt-1">{sub}</div>}
  </div>
);

export function SalesCertAnalytics({ analytics, compLabel, onFilterChange }) {
  const [region, setRegion] = useState("");
  const [competency, setCompetency] = useState("");
  const [bu, setBu] = useState("");
  const [manager, setManager] = useState("");
  const [dlBusy, setDlBusy] = useState(false);
  if (!analytics) return <div className="card p-10 text-center text-[#878787] text-sm">Loading analytics…</div>;
  const { invitees, attempts, averages, pass_rate_pct, passed, failed_below_threshold, question_difficulty, filter_options, filters_applied } = analytics;

  const applyFilters = (next) => {
    const merged = { region, competency, bu, manager, ...next };
    setRegion(merged.region);
    setCompetency(merged.competency);
    setBu(merged.bu);
    setManager(merged.manager);
    onFilterChange(merged);
  };

  const clearFilters = () => applyFilters({ region: "", competency: "", bu: "", manager: "" });

  const downloadCsv = async () => {
    setDlBusy(true);
    try {
      const params = new URLSearchParams();
      if (filters_applied?.region) params.set("region", filters_applied.region);
      if (filters_applied?.bu) params.set("bu", filters_applied.bu);
      if (filters_applied?.manager) params.set("manager", filters_applied.manager);
      if (filters_applied?.competency) params.set("competency", filters_applied.competency);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const r = await api.get(`/admin/salescert/analytics/export.csv${qs}`, { responseType: "blob" });
      // Filename from Content-Disposition, fallback to a sensible default
      const cd = r.headers?.["content-disposition"] || "";
      const match = /filename="([^"]+)"/.exec(cd);
      const fname = match ? match[1] : "salescert_analytics.csv";
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setDlBusy(false);
    }
  };

  const difficultyColor = (pct) => {
    if (pct >= 80) return "bg-[#D1FAE5] text-[#065F46]";
    if (pct >= 50) return "bg-[#FEF3C7] text-[#92400E]";
    return "bg-[#FEE2E2] text-[#991B1B]";
  };

  const activeFilterCount = ["region", "bu", "manager", "competency"].filter((k) => filters_applied?.[k]).length;

  const hasBus = (filter_options?.bus || []).length > 0;
  const hasManagers = (filter_options?.managers || []).length > 0;

  const scopeSubtitle = () => {
    const parts = [];
    if (filters_applied?.region) parts.push(filters_applied.region);
    if (filters_applied?.bu) parts.push(filters_applied.bu);
    if (filters_applied?.manager) parts.push(`Under ${filters_applied.manager}`);
    return parts.length ? parts.join(" · ") : "All cohorts";
  };

  return (
    <div className="space-y-6" data-testid="salescert-analytics">
      {/* Filter bar */}
      <div className="card p-4 flex flex-wrap items-center gap-3" data-testid="salescert-filters">
        <span className="overline">FILTERS</span>
        <select
          value={region}
          onChange={(e) => applyFilters({ region: e.target.value })}
          className="input !py-2 !px-3 text-xs min-w-[130px]"
          data-testid="salescert-filter-region"
        >
          <option value="">All regions</option>
          {(filter_options?.regions || []).map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        {hasBus && (
          <select
            value={bu}
            onChange={(e) => applyFilters({ bu: e.target.value })}
            className="input !py-2 !px-3 text-xs min-w-[150px]"
            data-testid="salescert-filter-bu"
          >
            <option value="">All BUs</option>
            {filter_options.bus.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        )}
        {hasManagers && (
          <select
            value={manager}
            onChange={(e) => applyFilters({ manager: e.target.value })}
            className="input !py-2 !px-3 text-xs min-w-[150px]"
            data-testid="salescert-filter-manager"
          >
            <option value="">All managers</option>
            {filter_options.managers.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        )}
        <select
          value={competency}
          onChange={(e) => applyFilters({ competency: e.target.value })}
          className="input !py-2 !px-3 text-xs min-w-[180px]"
          data-testid="salescert-filter-competency"
        >
          <option value="">All competencies (heatmap)</option>
          {(filter_options?.competencies || []).map((c) => (
            <option key={c} value={c}>{compLabel(c)}</option>
          ))}
        </select>
        {activeFilterCount > 0 && (
          <button
            onClick={clearFilters}
            className="text-xs uppercase tracking-widest text-[#EE2737] hover:text-[#0A0A0A] font-bold"
            data-testid="salescert-filter-clear"
          >
            Clear ({activeFilterCount})
          </button>
        )}
        <div className="flex-1" />
        <button
          onClick={downloadCsv}
          disabled={dlBusy}
          className="btn btn-secondary text-xs"
          data-testid="salescert-export-csv"
        >
          <Download size={12} /> {dlBusy ? "Preparing…" : "Download CSV"}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SalesCertStat label="INVITEES" value={invitees} sub={scopeSubtitle()} />
        <SalesCertStat
          label="FULLY COMPLETED"
          value={attempts.fully_completed}
          sub={`${attempts.mcq} MCQ · ${attempts.sales_pitch} Pitch · ${attempts.role_play} Role Play`}
        />
        <SalesCertStat label="PASS RATE" value={`${pass_rate_pct}%`} sub={`${passed} passed · ${failed_below_threshold} below 60%`} />
        <SalesCertStat label="AGGREGATE AVG" value={averages.aggregate ?? "—"} sub="Marks / 100 across completions" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SalesCertStat label="AVG MCQ" value={averages.mcq ?? "—"} sub="out of 50" />
        <SalesCertStat label="AVG SALES PITCH" value={averages.sales_pitch ?? "—"} sub="out of 25" />
        <SalesCertStat label="AVG ROLE PLAY" value={averages.role_play ?? "—"} sub="out of 25" />
      </div>

      <div className="card p-6">
        <div className="overline mb-2">
          MCQ QUESTION DIFFICULTY {filters_applied?.competency ? `· ${compLabel(filters_applied.competency)}` : ""}
        </div>
        <p className="text-xs text-[#525252] mb-4">Correct rate per question. Lowest first — these are the items most candidates trip on.</p>
        {question_difficulty.length === 0 ? (
          <div className="text-sm text-[#878787] italic py-4">No MCQ attempts recorded {activeFilterCount ? "for this cohort " : ""}yet.</div>
        ) : (
          <div className="space-y-2" data-testid="salescert-difficulty-heatmap">
            {question_difficulty.map((row) => (
              <div key={row.id} className="flex items-center gap-3 p-3 border border-[#E5E7EB]">
                <span className={`text-[11px] font-heading font-black px-2 py-1 uppercase tracking-widest whitespace-nowrap ${difficultyColor(row.correct_pct)}`}>
                  {row.correct_pct}%
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#0A0A0A] truncate">{row.question || row.id}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#878787] mt-0.5">
                    {compLabel(row.competency)} · {row.correct}/{row.attempts} correct
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


export function QuestionEditor({ type, question, competencies, onClose, onSaved }) {
  const isEdit = !!question;
  const [form, setForm] = useState(() => {
    if (question) return JSON.parse(JSON.stringify(question));
    if (type === "sjt") return { competency: competencies[0].key, scenario: "", options: [{ key: "A", text: "" }, { key: "B", text: "" }, { key: "C", text: "" }, { key: "D", text: "" }], scoring_key: { A: 5, B: 3, C: 2, D: 1 } };
    if (type === "mcq") return { competency: competencies[0].key, question: "", options: [{ key: "A", text: "" }, { key: "B", text: "" }, { key: "C", text: "" }, { key: "D", text: "" }], correct_option: "A" };
    return { competency: competencies[0].key, prompt: "" };
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setSaving(true); setErr("");
    try {
      await api.post(`/admin/questions/${type}`, form);
      await onSaved();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="question-editor">
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="overline !text-[#EE2737]">{isEdit ? "EDIT" : "NEW"} · {type.toUpperCase()}</div>
            <h2 className="font-heading font-black text-xl">{isEdit ? "Edit question" : "Add new question"}</h2>
          </div>
          <button onClick={onClose} className="text-[#525252] hover:text-[#0A0A0A]" data-testid="close-editor"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="overline mb-1.5 block">Competency</label>
            <select className="input" value={form.competency} onChange={(e) => setForm({ ...form, competency: e.target.value })} data-testid="field-competency">
              {competencies.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
            </select>
          </div>

          {type === "sjt" && (
            <>
              <div>
                <label className="overline mb-1.5 block">Scenario</label>
                <textarea className="input font-body" rows={4} value={form.scenario} onChange={(e) => setForm({ ...form, scenario: e.target.value })} placeholder="Describe the situation the participant must judge…" data-testid="field-scenario" />
              </div>
              <div>
                <label className="overline mb-1.5 block">Options & BARS scores (1–5)</label>
                <div className="space-y-2">
                  {form.options.map((o, i) => (
                    <div key={o.key} className="flex gap-2 items-center">
                      <span className="font-bold text-sm w-6">{o.key}.</span>
                      <input className="input flex-1" value={o.text} onChange={(e) => {
                        const opts = [...form.options]; opts[i] = { ...o, text: e.target.value };
                        setForm({ ...form, options: opts });
                      }} placeholder={`Option ${o.key}`} data-testid={`option-${o.key}`} />
                      <select className="input !w-20" value={form.scoring_key[o.key] ?? 1} onChange={(e) => setForm({ ...form, scoring_key: { ...form.scoring_key, [o.key]: parseInt(e.target.value, 10) } })} data-testid={`score-${o.key}`}>
                        {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-[#878787] mt-2">5 = Expert response · 1 = Weakest. Score each option independently — multiple options may share the same BARS score.</div>
              </div>
            </>
          )}

          {type === "mcq" && (
            <>
              <div>
                <label className="overline mb-1.5 block">Question</label>
                <textarea className="input font-body" rows={3} value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} placeholder="A knowledge question with one objectively correct answer…" data-testid="field-question" />
              </div>
              <div>
                <label className="overline mb-1.5 block">Options · select the correct answer</label>
                <div className="space-y-2">
                  {form.options.map((o, i) => (
                    <div key={o.key} className="flex gap-2 items-center">
                      <input type="radio" name="correct" checked={form.correct_option === o.key} onChange={() => setForm({ ...form, correct_option: o.key })} data-testid={`correct-${o.key}`} />
                      <span className="font-bold text-sm w-6">{o.key}.</span>
                      <input className="input flex-1" value={o.text} onChange={(e) => {
                        const opts = [...form.options]; opts[i] = { ...o, text: e.target.value };
                        setForm({ ...form, options: opts });
                      }} placeholder={`Option ${o.key}`} data-testid={`option-${o.key}`} />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {type === "open" && (
            <div>
              <label className="overline mb-1.5 block">Open-response prompt</label>
              <textarea className="input font-body" rows={5} value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} placeholder="A scenario the participant writes a 3-6 sentence response to. AI scores it 1-5 vs the competency rubric…" data-testid="field-prompt" />
            </div>
          )}

          {err && <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-xs px-3 py-2">{err}</div>}

          <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E7EB]">
            <button onClick={onClose} className="btn btn-ghost" data-testid="cancel-editor">Cancel</button>
            <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="save-question">
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create question"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
